<template>
  <div class="accountant-dashboard">
    <!-- Top Header with Brand Identity -->
    <div class="dashboard-header">
      <div class="header-content">
        <div class="brand-section">
          <div class="brand-icon">
            <feather-icon
              icon="DollarSignIcon"
              size="32"
              class="brand-logo"
            />
          </div>
          <div class="brand-info">
            <h1 class="brand-title">
              {{ $branding.company.name }}
            </h1>
            <p class="brand-subtitle">
              Tableau de bord Comptabilité
            </p>
          </div>
        </div>

        <div class="header-actions">
          <div class="time-display">
            <feather-icon
              icon="ClockIcon"
              size="16"
              class="time-icon"
            />
            <span class="current-time">{{ getCurrentTime() }}</span>
          </div>
          <b-button
            variant="outline-light"
            size="sm"
            class="refresh-btn"
            :disabled="loading"
            @click="loadAnalytics"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="16"
              :class="{ 'spinning': loading }"
            />
            {{ loading ? 'Chargement...' : 'Actualiser' }}
          </b-button>
        </div>
      </div>
    </div>

    <!-- Date Range Filter Section -->
    <div class="date-filter-section">
      <div class="quick-filters">
        <button
          class="quick-filter-btn"
          :class="{ active: selectedQuickFilter === 'today' }"
          @click="setQuickFilter('today')"
        >
          <feather-icon
            icon="CalendarIcon"
            size="14"
          />
          Aujourd'hui
        </button>
        <button
          class="quick-filter-btn"
          :class="{ active: selectedQuickFilter === 'week' }"
          @click="setQuickFilter('week')"
        >
          <feather-icon
            icon="CalendarIcon"
            size="14"
          />
          Cette semaine
        </button>
        <button
          class="quick-filter-btn"
          :class="{ active: selectedQuickFilter === 'month' }"
          @click="setQuickFilter('month')"
        >
          <feather-icon
            icon="CalendarIcon"
            size="14"
          />
          Ce mois
        </button>
        <button
          class="quick-filter-btn"
          :class="{ active: selectedQuickFilter === 'last_month' }"
          @click="setQuickFilter('last_month')"
        >
          <feather-icon
            icon="CalendarIcon"
            size="14"
          />
          Mois dernier
        </button>
      </div>

      <div class="custom-date-range">
        <label>Du</label>
        <input
          v-model="filters.start_date"
          type="date"
          @change="loadAnalytics"
        >
        <label>Au</label>
        <input
          v-model="filters.end_date"
          type="date"
          @change="loadAnalytics"
        >
      </div>
    </div>

    <!-- Loading Overlay -->
    <div
      v-if="loading"
      class="loading-overlay"
    >
      <div class="loading-content">
        <div class="loading-spinner" />
        <p class="loading-text">
          Chargement des statistiques...
        </p>
      </div>
    </div>

    <!-- Summary Cards -->
    <div
      v-if="!loading"
      class="summary-cards"
    >
      <div class="summary-card expense">
        <div class="card-icon">
          <feather-icon
            icon="TrendingDownIcon"
            size="28"
          />
        </div>
        <div class="card-content">
          <div class="card-label">
            Dépenses Totales
          </div>
          <div class="card-value">
            {{ formatAmount(analytics.summary.total_amount) }} DT
          </div>
        </div>
      </div>

      <div class="summary-card count">
        <div class="card-icon">
          <feather-icon
            icon="FileTextIcon"
            size="28"
          />
        </div>
        <div class="card-content">
          <div class="card-label">
            Nombre de Transactions
          </div>
          <div class="card-value">
            {{ analytics.summary.count }}
          </div>
        </div>
      </div>

      <div class="summary-card average">
        <div class="card-icon">
          <feather-icon
            icon="ActivityIcon"
            size="28"
          />
        </div>
        <div class="card-content">
          <div class="card-label">
            Moyenne par Transaction
          </div>
          <div class="card-value">
            {{ formatAmount(analytics.summary.average) }} DT
          </div>
        </div>
      </div>

      <div class="summary-card vendors">
        <div class="card-icon">
          <feather-icon
            icon="UsersIcon"
            size="28"
          />
        </div>
        <div class="card-content">
          <div class="card-label">
            Fournisseurs Actifs
          </div>
          <div class="card-value">
            {{ analytics.by_vendor ? analytics.by_vendor.length : 0 }}
          </div>
        </div>
      </div>
    </div>

    <!-- Charts Section -->
    <div
      v-if="!loading"
      class="charts-section"
    >
      <!-- Category Breakdown -->
      <div class="chart-card">
        <h3>
          <feather-icon
            icon="PieChartIcon"
            size="20"
          />
          Répartition par Catégorie
        </h3>
        <apexchart
          v-if="categoryChartOptions.series && categoryChartOptions.series.length > 0"
          type="donut"
          height="350"
          :options="categoryChartOptions.chartOptions"
          :series="categoryChartOptions.series"
        />
        <div
          v-else
          class="no-data-message"
        >
          <feather-icon
            icon="InboxIcon"
            size="48"
          />
          <p>Aucune donnée disponible</p>
        </div>
      </div>

      <!-- Trend Chart -->
      <div class="chart-card">
        <h3>
          <feather-icon
            icon="TrendingUpIcon"
            size="20"
          />
          Évolution des Dépenses
        </h3>
        <apexchart
          v-if="trendChartOptions.series && trendChartOptions.series.length > 0"
          type="area"
          height="350"
          :options="trendChartOptions.chartOptions"
          :series="trendChartOptions.series"
        />
        <div
          v-else
          class="no-data-message"
        >
          <feather-icon
            icon="InboxIcon"
            size="48"
          />
          <p>Aucune donnée disponible</p>
        </div>
      </div>
    </div>

    <!-- Tables Section -->
    <div
      v-if="!loading"
      class="tables-section"
    >
      <!-- Top Categories Table -->
      <div class="table-card">
        <h3>
          <feather-icon
            icon="TagIcon"
            size="20"
          />
          Top Catégories
        </h3>
        <table
          v-if="analytics.by_category && analytics.by_category.length > 0"
          class="analytics-table"
        >
          <thead>
            <tr>
              <th>Catégorie</th>
              <th class="text-right">
                Montant
              </th>
              <th class="text-center">
                Transactions
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(category, index) in analytics.by_category.slice(0, 5)"
              :key="category.category_name || `category-${index}`"
            >
              <td>
                <span
                  class="category-color-dot"
                  :style="{ backgroundColor: category.category_color || '#6366f1' }"
                />
                {{ category.category_name || 'Sans catégorie' }}
              </td>
              <td class="text-right amount-cell">
                {{ formatAmount(category.total) }} DT
              </td>
              <td class="text-center">
                {{ category.count }}
              </td>
            </tr>
          </tbody>
        </table>
        <div
          v-else
          class="no-data-message"
        >
          <feather-icon
            icon="InboxIcon"
            size="32"
          />
          <p>Aucune catégorie disponible</p>
        </div>
      </div>

      <!-- Top Vendors Table -->
      <div class="table-card">
        <h3>
          <feather-icon
            icon="TruckIcon"
            size="20"
          />
          Top Fournisseurs
        </h3>
        <table
          v-if="analytics.by_vendor && analytics.by_vendor.length > 0"
          class="analytics-table"
        >
          <thead>
            <tr>
              <th>Fournisseur</th>
              <th class="text-right">
                Montant
              </th>
              <th class="text-center">
                Transactions
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(vendor, index) in analytics.by_vendor.slice(0, 5)"
              :key="vendor.vendor_name || `vendor-${index}`"
            >
              <td>{{ vendor.vendor_name || 'Non spécifié' }}</td>
              <td class="text-right amount-cell">
                {{ formatAmount(vendor.total) }} DT
              </td>
              <td class="text-center">
                {{ vendor.count }}
              </td>
            </tr>
          </tbody>
        </table>
        <div
          v-else
          class="no-data-message"
        >
          <feather-icon
            icon="InboxIcon"
            size="32"
          />
          <p>Aucun fournisseur disponible</p>
        </div>
      </div>
    </div>

    <!-- Quick Actions -->
    <div class="quick-actions">
      <h3>
        <feather-icon
          icon="ZapIcon"
          size="20"
        />
        Actions Rapides
      </h3>
      <div class="actions-grid">
        <router-link
          to="/expenses"
          class="action-card"
        >
          <feather-icon
            icon="ListIcon"
            size="24"
          />
          <span>Toutes les dépenses</span>
        </router-link>
        <router-link
          to="/expenses/create"
          class="action-card create"
        >
          <feather-icon
            icon="PlusCircleIcon"
            size="24"
          />
          <span>Nouvelle dépense</span>
        </router-link>
        <router-link
          to="/expenses/vendors"
          class="action-card"
        >
          <feather-icon
            icon="UsersIcon"
            size="24"
          />
          <span>Fournisseurs</span>
        </router-link>
        <router-link
          to="/expenses/categories"
          class="action-card"
        >
          <feather-icon
            icon="TagIcon"
            size="24"
          />
          <span>Catégories</span>
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton } from 'bootstrap-vue'
import axios from 'axios'
import VueApexCharts from 'vue-apexcharts'

export default {
  name: 'AccountantDashboard',
  components: {
    BButton,
    apexchart: VueApexCharts,
  },
  data() {
    const today = new Date()
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1)
    const [startDate] = monthStart.toISOString().split('T')
    const [endDate] = today.toISOString().split('T')

    return {
      loading: false,
      selectedQuickFilter: 'month',
      currentTime: new Date(),
      filters: {
        start_date: startDate,
        end_date: endDate,
      },
      analytics: {
        summary: {
          total_amount: 0,
          count: 0,
          average: 0,
        },
        by_category: [],
        by_vendor: [],
        trend: [],
      },
    }
  },
  computed: {
    categoryChartOptions() {
      if (!this.analytics.by_category || this.analytics.by_category.length === 0) {
        return {
          series: [],
          chartOptions: {},
        }
      }

      // Use colors from API or fallback to default colors
      const categoryColors = this.analytics.by_category.map(c => c.category_color || null).filter(Boolean)
      const defaultColors = ['#ff9f43', '#0A2634', '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899', '#eab308']
      const colors = categoryColors.length > 0 ? categoryColors : defaultColors

      return {
        series: this.analytics.by_category.map(c => parseFloat(c.total)),
        chartOptions: {
          chart: {
            type: 'donut',
          },
          labels: this.analytics.by_category.map(c => c.category_name || 'Sans catégorie'),
          colors: colors.length >= this.analytics.by_category.length ? colors : defaultColors,
          legend: {
            position: 'bottom',
            fontSize: '14px',
          },
          plotOptions: {
            pie: {
              donut: {
                size: '65%',
                labels: {
                  show: true,
                  total: {
                    show: true,
                    label: 'Total',
                    formatter: () => `${this.formatAmount(this.analytics.summary.total_amount)} DT`,
                  },
                },
              },
            },
          },
          dataLabels: {
            enabled: true,
            formatter(val) {
              return `${val.toFixed(1)}%`
            },
          },
          tooltip: {
            y: {
              formatter: value => `${parseFloat(value).toFixed(3)} DT`,
            },
          },
          responsive: [
            {
              breakpoint: 480,
              options: {
                chart: {
                  width: 300,
                },
                legend: {
                  position: 'bottom',
                },
              },
            },
          ],
        },
      }
    },
    trendChartOptions() {
      if (!this.analytics.trend || this.analytics.trend.length === 0) {
        return {
          series: [],
          chartOptions: {},
        }
      }

      return {
        series: [
          {
            name: 'Dépenses',
            data: this.analytics.trend.map(t => parseFloat(t.total)),
          },
        ],
        chartOptions: {
          chart: {
            type: 'area',
            toolbar: {
              show: false,
            },
          },
          colors: ['#ff9f43'],
          dataLabels: {
            enabled: false,
          },
          stroke: {
            curve: 'smooth',
            width: 3,
          },
          fill: {
            type: 'gradient',
            gradient: {
              opacityFrom: 0.6,
              opacityTo: 0.1,
            },
          },
          xaxis: {
            categories: this.analytics.trend.map(t => this.formatDate(t.date)),
          },
          yaxis: {
            labels: {
              formatter: value => `${value.toFixed(0)} DT`,
            },
          },
          tooltip: {
            y: {
              formatter: value => `${parseFloat(value).toFixed(3)} DT`,
            },
          },
          grid: {
            borderColor: '#f1f1f1',
          },
        },
      }
    },
  },
  mounted() {
    this.loadAnalytics()
    // Update time every second
    setInterval(() => {
      this.currentTime = new Date()
    }, 1000)
  },
  methods: {
    getCurrentTime() {
      return this.currentTime.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    },
    async loadAnalytics() {
      this.loading = true
      try {
        const params = {
          start_date: this.filters.start_date,
          end_date: this.filters.end_date,
        }

        const response = await axios.get('/api/analytics/expenses/', { params })
        this.analytics = response.data
      } catch (error) {
        console.error('Error loading analytics:', error)
        this.$toast({
          component: () => import('@core/components/toastification/ToastificationContent.vue'),
          props: {
            title: 'Erreur',
            icon: 'AlertCircleIcon',
            text: 'Erreur lors du chargement des analyses',
            variant: 'danger',
          },
        })
      } finally {
        this.loading = false
      }
    },
    setQuickFilter(filter) {
      this.selectedQuickFilter = filter
      const today = new Date()
      const [todayDate] = today.toISOString().split('T')

      switch (filter) {
        case 'today': {
          this.filters.start_date = todayDate
          this.filters.end_date = todayDate
          break
        }
        case 'week': {
          const weekStart = new Date(today)
          weekStart.setDate(today.getDate() - today.getDay())
          const [weekStartDate] = weekStart.toISOString().split('T')
          this.filters.start_date = weekStartDate
          this.filters.end_date = todayDate
          break
        }
        case 'month': {
          const monthStart = new Date(today.getFullYear(), today.getMonth(), 1)
          const [monthStartDate] = monthStart.toISOString().split('T')
          this.filters.start_date = monthStartDate
          this.filters.end_date = todayDate
          break
        }
        case 'last_month': {
          const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1)
          const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0)
          const [lastMonthStartDate] = lastMonthStart.toISOString().split('T')
          const [lastMonthEndDate] = lastMonthEnd.toISOString().split('T')
          this.filters.start_date = lastMonthStartDate
          this.filters.end_date = lastMonthEndDate
          break
        }
        default:
          break
      }

      this.loadAnalytics()
    },
    formatAmount(amount) {
      return parseFloat(amount || 0).toFixed(3)
    },
    formatDate(dateStr) {
      const date = new Date(dateStr)
      return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })
    },
  },
}
</script>

<style scoped>
.accountant-dashboard {
  padding: 0;
  max-width: 100%;
  min-height: 100vh;
  background: #f8fafc;
}

/* Header Styles */
.dashboard-header {
  background: linear-gradient(135deg, #0A2634 0%, #1a3a4a 100%);
  padding: 24px 32px;
  margin-bottom: 24px;
  border-radius: 0 0 16px 16px;
  box-shadow: 0 4px 20px rgba(10, 38, 52, 0.3);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.brand-section {
  display: flex;
  align-items: center;
  gap: 16px;
}

.brand-icon {
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #ff9f43 0%, #ffb976 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 4px 15px rgba(255, 159, 67, 0.4);
}

.brand-title {
  font-size: 28px;
  font-weight: 700;
  color: white;
  margin: 0;
}

.brand-subtitle {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.85);
  margin: 4px 0 0 0;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.time-display {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.15);
  padding: 8px 16px;
  border-radius: 20px;
  color: white;
  font-weight: 500;
}

.refresh-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  border-color: rgba(255, 255, 255, 0.5) !important;
  color: white !important;
}

.refresh-btn:hover {
  background: rgba(255, 255, 255, 0.1) !important;
}

.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Date Filter Section */
.date-filter-section {
  background: white;
  padding: 20px 32px;
  margin: 0 24px 24px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.quick-filters {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.quick-filter-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: #f3f4f6;
  color: #4b5563;
  border: 2px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 14px;
  font-weight: 500;
}

.quick-filter-btn:hover {
  background: #e5e7eb;
}

.quick-filter-btn.active {
  background: #fff7ed;
  color: #c2410c;
  border-color: #ff9f43;
}

.custom-date-range {
  display: flex;
  gap: 12px;
  align-items: center;
}

.custom-date-range label {
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
}

.custom-date-range input {
  padding: 10px 14px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s;
}

.custom-date-range input:focus {
  outline: none;
  border-color: #ff9f43;
}

/* Loading Overlay */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.loading-content {
  text-align: center;
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #f3f4f6;
  border-top-color: #ff9f43;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

.loading-text {
  color: #6b7280;
  font-size: 16px;
}

/* Summary Cards */
.summary-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
  padding: 0 24px;
  margin-bottom: 24px;
}

.summary-card {
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  gap: 16px;
  transition: transform 0.2s, box-shadow 0.2s;
}

.summary-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.card-icon {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.summary-card.expense .card-icon {
  background: #fff7ed;
  color: #ff9f43;
}

.summary-card.count .card-icon {
  background: #fef3c7;
  color: #d97706;
}

.summary-card.average .card-icon {
  background: #dbeafe;
  color: #2563eb;
}

.summary-card.vendors .card-icon {
  background: #d1fae5;
  color: #059669;
}

.card-label {
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 4px;
}

.card-value {
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
}

/* Charts Section */
.charts-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 20px;
  padding: 0 24px;
  margin-bottom: 24px;
}

.chart-card {
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.chart-card h3 {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 20px 0;
  font-size: 18px;
  color: #1f2937;
}

/* Tables Section */
.tables-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 20px;
  padding: 0 24px;
  margin-bottom: 24px;
}

.table-card {
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.table-card h3 {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 16px 0;
  font-size: 18px;
  color: #1f2937;
}

.analytics-table {
  width: 100%;
  border-collapse: collapse;
}

.analytics-table th {
  text-align: left;
  padding: 12px;
  background: #f9fafb;
  color: #6b7280;
  font-weight: 600;
  font-size: 14px;
  border-bottom: 2px solid #e5e7eb;
}

.analytics-table td {
  padding: 12px;
  border-bottom: 1px solid #f3f4f6;
  font-size: 14px;
}

.analytics-table tbody tr:hover {
  background: #f9fafb;
}

.text-right {
  text-align: right;
}

.text-center {
  text-align: center;
}

.amount-cell {
  font-weight: 600;
  color: #ff9f43;
  font-family: 'Courier New', monospace;
}

.category-color-dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 8px;
  vertical-align: middle;
}

/* Quick Actions */
.quick-actions {
  padding: 0 24px 24px;
}

.quick-actions h3 {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 16px 0;
  font-size: 18px;
  color: #1f2937;
}

.actions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.action-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  text-decoration: none;
  color: #374151;
  font-weight: 500;
  transition: all 0.2s;
}

.action-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  color: #ff9f43;
}

.action-card.create {
  background: linear-gradient(135deg, #ff9f43 0%, #ff8c00 100%);
  color: white;
}

.action-card.create:hover {
  color: white;
  box-shadow: 0 4px 12px rgba(255, 159, 67, 0.4);
}

/* No Data Message */
.no-data-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #9ca3af;
}

.no-data-message p {
  margin: 12px 0 0 0;
  font-size: 14px;
}

/* Responsive */
@media (max-width: 768px) {
  .dashboard-header {
    padding: 20px;
    border-radius: 0;
  }

  .header-content {
    flex-direction: column;
    align-items: flex-start;
  }

  .date-filter-section {
    margin: 0 16px 20px;
    padding: 16px;
    flex-direction: column;
    align-items: stretch;
  }

  .quick-filters {
    justify-content: center;
  }

  .custom-date-range {
    flex-wrap: wrap;
    justify-content: center;
  }

  .summary-cards,
  .charts-section,
  .tables-section,
  .quick-actions {
    padding: 0 16px;
  }

  .charts-section {
    grid-template-columns: 1fr;
  }

  .tables-section {
    grid-template-columns: 1fr;
  }
}
</style>
